/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, ReactElement, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {List, makeStyles, Typography} from '@material-ui/core';
import isNil from 'lodash.isnil';
import isString from 'lodash.isstring';
import isUndefined from 'lodash.isundefined';
import {getAssetState} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import {isCVSAsset, isScDevice} from '@ion/core/src/services/ArcGISUtils';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import Device from '@ion/core/src/services/Device';
import {AssetType, BaseAsset, CVSAssetType, GenericObject} from '@ion/core/src/types/entities';
import {getEventSpeed, getUserUnit, isImperial} from '@ion/core/src/units/helpers';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {isNonZeroNumber} from '@ion/core/src/utils/NumberUtil';
import {stringToNumber} from '@ion/core/src/utils/StringUtil';
import BatteryChargeLevel from '@ion/dls/src/components/battery/BatteryChargeLevel';
import NoGpsStatus from '@ion/dls/src/components/gps/NoGpsStatus';
import AssetStatusItem from '@ion/dls/src/components/sidepanel/AssetStatusItem';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {AssetTypeIcon} from '@ion/dls/src/icons/AssetTypeIcon';
import {MaterialExpandLessIcon} from '@ion/dls/src/icons/MaterialExpandLessIcon';
import {MaterialExpandMoreIcon} from '@ion/dls/src/icons/MaterialExpandMoreIcon';
import {PoorGpsIcon} from '@ion/dls/src/icons/PoorGpsIcon';
import CardItemRow from '@ion/dls/src/layout/CardItemRow';
import Address from '@ion/dls/src/maps/components/Address';
import {getDeviceStatus, getSCStatus} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {useAssetSpeed} from '@ion/dls/src/maps/hooks/useAssetSpeed';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {useThemeMode} from '@ion/dls/src/theme/useThemeContext';
import {AssetLabelTypes, DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {formatSpeed, getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';

interface AssetCardListContentProps {
  asset: Device;
  labels?: AssetLabelTypes;
  onContainsClick?: (asset: CVSAssetType) => void;
  ContainedComponent?: ReactElement;
  showContainedComponent?: boolean;
  assetDetailView?: boolean;
  isTrackingBeta?: boolean;
  toolPanelOpen?: {
    assetId?: number;
    open?: boolean;
  }[];
  onSelectAssociatedAsset: any;
  assignedAssetsPanelsOpen?: string;
  onToggleAssignedAssetsPanel?: Function;
}

const useStyles = makeStyles(({
  dls: {
    motion: {
      element: {
        smallExpansion: {
          close: closeSmallExpansion,
          open: openSmallExpansion
        }
      }
    }
  }
}: DLSTheme) => ({
  countIconContainer : {
    display    : 'flex',
    alignItems : 'center'
  },
  panelClosed : {
    transition : `transform ${closeSmallExpansion}`
  },
  panelOpen : {
    transform  : 'rotateZ(180deg)',
    transition : `transform ${openSmallExpansion}`
  },
  associatedAssetsText : {
    color      : dlsColor('aaBlue'),
    padding    : '4px 0px !important',
    fontFamily : 'Roboto',
    fontSize   : '14px',
    lineHeight : '24px',
    maxWidth   : '245px',
    textAlign  : 'right',
    display    : 'inline',
    fontWeight : 400
  },
  associatedAssets : {
    color     : dlsColor('aaBlue'),
    width     : '50%',
    display   : 'inline',
    '&:hover' : {
      cursor : 'pointer',
      '& p'  : {
        textDecoration : 'underline'
      }
    }
  },
  toolsAlignment : {
    textAlign : 'end'
  },
  assignedAssetsContainer : {
    borderTop : `1px dashed ${dlsColor('grey4')}`,
    marginTop : 10
  },
  iconValueWrapper : {
    color      : dlsColor('grey2'),
    fontFamily : 'Roboto',
    fontWeight : 400,
    fontSize   : '14px',
    lineHeight : '24px',
    display    : 'inline-flex',
    flexWrap   : 'wrap',
    alignItems : 'center',
    gap        : '12px'

  },
  sciStatus : {
    paddingLeft : '10px'
  },
  lastReported : {
    '& p' : {
      ...dlsAlt('typography', 'label3')
    }
  },
  row : {
    ...dlsTypography('body3'),
    lineHeight : '24px',
    padding    : '0px',
    color      : `${dlsColor('grey2')}`
  },
  addressContainer : {
    display    : 'flex',
    alignItems : 'center'
  },
  distanceTypography : {
    textAlign     : 'right',
    fontWeight    : 700,
    fontSize      : '12px',
    lineHeight    : '16px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    color         : `${dlsColor('grey2')} !important`,
    padding       : '0 !important'
  },
  toolItem : {
    color      : dlsColor('grey3'),
    width      : '100%',
    display    : 'flex',
    alignItems : 'center',
    padding    : '3px 0',
    '&:hover'  : {
      cursor : 'pointer',
      '& p'  : {
        textDecoration : 'underline'
      }
    }
  },
  iconContainer : {
    padding : '3px 2px 0'
  },
  toolText : {
    color        : dlsColor('grey3'),
    fontSize     : '14px',
    lineHeight   : '16px',
    maxWidth     : '290px',
    textOverflow : 'ellipsis',
    whiteSpace   : 'nowrap',
    overflow     : 'hidden'

  }
}));

/**
 * parseAssociatedAsset normalizes one raw associated-asset entry (from
 * `asset.getDevice().assets`) into the shape both this file's own
 * type-grouped panel and other consumers (e.g. the current-theme Tags
 * accordion's Assigned tab) need - extracted here so both share one
 * id/type-inference pass rather than each re-deriving it.
 *
 * ```typescript
 *
 *   import {parseAssociatedAsset} from
 *     '@/routes/tracking/AssetCardListContent';
 *   ...
 *
 *   const parsed = parseAssociatedAsset(currentAsset.assetId, rawEntry);
 *
 * ```
 *
 * @param originatingAssetId - the id of the asset these entries belong to
 * @param rawAsset            - one raw entry from `asset.getDevice().assets`
 * @returns The normalized `{originatingAssetId, id, name, type}` entry
 */
export const parseAssociatedAsset = (originatingAssetId: string, {
  id,
  name,
  type,
  href,
  title,
  rel
}: GenericObject) => ({
  originatingAssetId,
  id   : stringToNumber(id) || entityId(href),
  name : name || title,
  type : type || rel.toLowerCase()
});

const parseAssociatedAssets = (currentAsset, associatedAssets) => {
  if (!associatedAssets) return null;

  const typeToAssetsMap = new Map();
  associatedAssets.forEach(function(rawAsset) {
    const associatedAssetData = parseAssociatedAsset(currentAsset.assetId, rawAsset);
    if (typeToAssetsMap.has(associatedAssetData.type)) {
      typeToAssetsMap.get(associatedAssetData.type).push(associatedAssetData);
    } else {
      typeToAssetsMap.set(associatedAssetData.type, [associatedAssetData]);
    }
  });

  return typeToAssetsMap;
};

/**
 * @AssetCardListContent
 *
 * The content of the list items in the asset card differ in content but
 * share styles depending on what's being displayed. So we can reuse it with
 * this component.
 *
 * ```typescript
 *
 *   import React from 'react';
 *   import AssetCardListContent from '@ion/dls/src/components/cards/AssetCardListContent';
 *   import { theme } from '@ion/dls/src/theme/theme';
 *
 *   <AssetCardListContent asset={asset}
 *     labels={labels}
 *     toolPanelOpen={toolPanelOpen}
 *     onContainsClick={onContainsClick} />
 *
 * ```
 *
 */
const AssetCardListContent = (props: AssetCardListContentProps) => {
  const classes     = useStyles();
  const showPopup   = useSelector(({tracking}) => tracking.findNearbyState.showPopup);
  const {isDesktop} = useMedia();
  const {mode}      = useThemeMode();
  const getSpeed    = useAssetSpeed();

  const {
    asset: currentAsset,
    labels,
    toolPanelOpen,
    assignedAssetsPanelsOpen,
    onToggleAssignedAssetsPanel,
    onContainsClick,
    ContainedComponent,
    assetDetailView,
    showContainedComponent=true,
    onSelectAssociatedAsset,
    isTrackingBeta
  } = props;

  const asset      = currentAsset instanceof Device ? currentAsset : new Device(currentAsset);
  const isSupplyChainDevice = asset?.isScDevice();

  const {
    id,
    city,
    state,
    street,
    status,
    distance,
    routeName,
    assets: assignedAssets
  } = asset?.getDevice();
  const parsedAssignedAssets = isString(assignedAssets) ? safeJSONParse(assignedAssets) : assignedAssets;

  const isAssignedAssetsPanelOpen = (id) => {
    return assignedAssetsPanelsOpen === id;
  };

  const device = asset?.getDevice();
  const getSpeedVal = (asset: BaseAsset): string => {
    const {speed, units} = getEventSpeed(device);
    return formatSpeed(speed, units);
  };

  const mappedAssetStatus = getAssetState(device);
  const speedVal          = getSpeed(device, getSpeedVal(device));
  const hasTagReport      = isScDevice(asset?.getDevice?.()) && asset?.getAssetValue('tagReports');

  const isToolOpen = (items, asset): { class: string; status: boolean; } => {
    const status = items.find(({assetId, open}) => assetId === asset?.getAssetValue?.('id') && open);
    return {class: classes[status ? 'panelOpen' : 'panelClosed'], status};
  };
  const [assetToolOpen, setAssetToolOpen] = useState(isToolOpen(toolPanelOpen, device));

  function onCollapseClick(e: MouseEvent) {
    if (!showPopup) {
      e.stopPropagation();
      onContainsClick(asset?.getDevice());
    }
  };

  function onAssignedAssetsPanelClick(e: MouseEvent) {
    e.stopPropagation();
    onToggleAssignedAssetsPanel(id);
  };

  useEffect(() => {
    const isOpen = isToolOpen(toolPanelOpen, asset);
    setAssetToolOpen(isOpen);
  }, [toolPanelOpen]);

  /**
   * Builds the jsx components necessary to display any associated assets dynamically.
   * @returns jsx components for associated assets (if there are any, otherwise empty div)
   */
  const associatedAssets = useMemo(() => {
    const currentAsset = asset instanceof Device ? asset.getDevice() : asset;
    if (!parsedAssignedAssets) return null;

    const typeToAssetsMap = parseAssociatedAssets(currentAsset, parsedAssignedAssets);

    return (
      <div className={classes.assignedAssetsContainer}>
        {Array.from(typeToAssetsMap).map(([type, assets]) => (
          assets.map((associatedAsset) => (
            <div key={associatedAsset.name} className={classes.toolItem} onClick={function(e) {
              e.preventDefault();
              e.stopPropagation();
              onSelectAssociatedAsset(e, associatedAsset);
            }}>
              <div className={classes.iconContainer}>
                <AssetTypeIcon assetType={AssetType[type || 'Tool']}
                  color={dlsColor('grey3')}
                  size={IconSize.small} />
              </div>
              <Typography className={classes.toolText}>
                {associatedAsset.name}
              </Typography>
            </div>
          ))
        ))}
      </div>
    );
  }, [asset, parsedAssignedAssets, mode]);

  const getEvChargeState = (asset) => {
    const isCVSAsset      = asset?.isCVSAsset();
    const chargingState   = asset?.getAssetValue('evDerivedChargingState');
    const remainingCharge = asset?.getAssetValue('evBatteryPackRemainingCharge');
    const validCharging   = isCVSAsset && !isNil(chargingState);
    const validPercent    = isCVSAsset && !isNil(remainingCharge);
    return {
      evIsCharging         : validCharging ? chargingState.toUpperCase() === 'ON' : null,
      evChargeLevelPercent : validPercent ? remainingCharge : null
    };
  };
  const {evIsCharging, evChargeLevelPercent} = getEvChargeState(asset);
  const scDeviceBattery                      = asset?.batteryLevelValue();
  const deviceBattery                        = asset?.getAssetValue('batteryRemainingPercent');

  const assetStatus = asset?.getItemValue({deviceAttribute: 'assetStatus', cvsAttribute: 'assetStatus', isCVSAttribute: false});
  const distanceUOM = translate(`common:units.${isImperial(getUserUnit()) ? 'mi' : 'km'}`);
  const isValidGPS  = isNonZeroNumber(asset.latitude()) && isNonZeroNumber(asset.longitude());
  const getAddress = useMemo(function() {
    if (isValidGPS) {
      const poorGpsTooltipTitle = isDesktop ? translate('tracking:list.detailed.poorGpsQuality') : '';
      const showIcon = (
        !asset.isGpsValid() && isValidGPS
      );
      return (
        <Address isAltVariant
          street={street}
          city={city} state={state}
          icon={showIcon && <PoorGpsIcon />}
          toolTipTitle={poorGpsTooltipTitle}/>
      );
    }
    return (<NoGpsStatus isTextRed />);
  }, [currentAsset, isDesktop]);

  const renderExpandCollapseIcon = () => {
    const assignedAssetsCount = parsedAssignedAssets?.length;
    if (isAssignedAssetsPanelOpen(id)) {
      return (
        <div className={classes.countIconContainer}>
          <Typography className={classes.associatedAssetsText}>
            {`(${assignedAssetsCount})`}
          </Typography>
          <MaterialExpandLessIcon color={dlsColor('grey3')} />
        </div>
      );
    } else {
      return (
        <div className={classes.countIconContainer}>
          <Typography className={classes.associatedAssetsText}>
            {`(${assignedAssetsCount})`}
          </Typography>
          <MaterialExpandMoreIcon color={dlsColor('grey3')} />
        </div>
      );
    }
  };

  const isSCIDevice = !isTrackingBeta && isSupplyChainDevice;
  
  return (
    <>
      <List style={{paddingBottom: 0}}>
        <Render.If condition={(!assetDetailView && !isSupplyChainDevice && (distance || distance >= 0))}>
          <CardItemRow label={labels.distance}
            isCondensedVariant
            isBottomMargin
            isLabelColon
            value={
              <BodyTypography className={classes.distanceTypography}>
                {`${distance} ${distanceUOM} ${labels.away}`}
              </BodyTypography>
            } />
        </Render.If>
        <CardItemRow label={labels.status ?? assetStatus}
          isCenterAligned={false}
          isBottomMargin={false}
          isCondensedVariant
          isLabelColon
          value={
            <AssetStatusItem assetStatus={isSCIDevice ? getSCStatus(status, device) : mappedAssetStatus}
              value={isSCIDevice ? getDeviceStatus(asset?.status(), device) : mappedAssetStatus}
              speed={speedVal}
              dateTime={getFormattedDate(asset?.eventTime()).toUpperCase()}
              scDevice={isSupplyChainDevice}
              isCustomVariant
              fromCard />
          } />
        <Render.If condition={isValidGPS}>
          <CardItemRow label={labels.address}
            value={getAddress}
            isBottomMargin={false}
            isCenterAligned={false}
            isCondensedVariant
            isLabelColon/>
        </Render.If>
        <CardItemRow label={labels.group}
          value={asset?.accountName()}
          isBottomMargin={false}
          isCondensedVariant
          isLabelColon />
        {asset.isCVSAsset() && !!routeName && (
          <CardItemRow label={labels.routeName}
            value={routeName}
            isBottomMargin={false}
            isCondensedVariant
            isLabelColon />
        )}
        <Render.If condition={isSupplyChainDevice}>
          <CardItemRow label={labels.batteryLevel}
            value={(
              <div className={classes.iconValueWrapper}>
                <BatteryChargeLevel value={scDeviceBattery}
                  iconId={`device-${asset?.OBJECTID()}`}
                  valueComponent={
                    <span className={classes.row}>
                      {`${scDeviceBattery}${asset?.batteryLevelUnits()}`}
                    </span>
                  }
                  isCardVariant />
              </div>
            )}
            isCenterAligned={false}
            isBottomMargin={false}
            isCondensedVariant
            isLabelColon />
        </Render.If>
        {
          !!assignedAssets?.length && (
            <>
              <CardItemRow label={translate('tracking:list.detailed.assignedAssets')}
                value={renderExpandCollapseIcon()}
                handleClick={onAssignedAssetsPanelClick}
                isBottomMargin={false}
                isCondensedVariant
                isLabelColon />
              <div className={classes.toolsAlignment}>
                {isAssignedAssetsPanelOpen(id) && associatedAssets}
              </div>
            </>
          )
        }
        {!isNil(deviceBattery) &&
           <CardItemRow label={labels.deviceBattery}
             value={(
               <div className={classes.iconValueWrapper}>
                 <BatteryChargeLevel value={deviceBattery}
                   iconId={`device-${asset?.OBJECTID()}`}
                   valueComponent={
                     <span className={classes.row}>
                       {`${deviceBattery}${asset?.batteryLevelUnits()}`}
                     </span>
                   }
                   isCardVariant />
               </div>
             )}
             isCenterAligned={false}
             isBottomMargin={false}
             isCondensedVariant
             isLabelColon />
        }
        {!isNil(evChargeLevelPercent) &&
           <CardItemRow label={labels.batteryLevel}
             value={
               <BatteryChargeLevel isCharging={evIsCharging}
                 value={evChargeLevelPercent}
                 iconId={`evChargeLevelPercentAssetCard-${asset?.OBJECTID()}`}
                 valueComponent={
                   <span className={classes.row}>
                     {`${evChargeLevelPercent} %`}
                   </span>
                 }
                 isCardVariant />
             }
             isBottomMargin={false}
             isCondensedVariant
             isLabelColon />
        }
        {(isCVSAsset(asset?.getDevice?.()) || hasTagReport) && (
          <CardItemRow label={labels.iOnTags}
            value={
              assetToolOpen?.status
                ? <MaterialExpandLessIcon color={dlsColor('grey3')} />
                : <MaterialExpandMoreIcon color={dlsColor('grey3')} />
            }
            handleClick={onCollapseClick}
            isBottomMargin={false}
            isCondensedVariant
            isLabelColon />
        )}
        {showContainedComponent && ContainedComponent && (<ContainedComponent {...props} />)}
      </List>
    </>
  );
};

export default AssetCardListContent;
